import React, { createContext, useContext, useMemo } from 'react';
import { useAuth0, User, RedirectLoginOptions, LogoutOptions, IdToken } from '@auth0/auth0-react';

function decodeJwt(token: string): Record<string, unknown> | undefined {
  if (!token) return undefined;
  const parts = token.split('.');
  if (parts.length !== 3) return undefined;
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(payload);
    return JSON.parse(decoded);
  }
  catch (e) {
    return undefined;
  }
}

interface UnifiedAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  getAccessTokenSilently: () => Promise<string | undefined>;
  getIdTokenClaims: () => Promise<IdToken | undefined>;
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
  logout: (options?: LogoutOptions) => void;
  user: User | IdToken | undefined;
  loginWithPopup: () => Promise<void>;
}

const UnifiedAuthContext = createContext<UnifiedAuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  getAccessTokenSilently: async () => undefined,
  getIdTokenClaims: async () => undefined as IdToken | undefined,
  loginWithRedirect: async () => Promise.resolve(),
  logout: () => { throw new Error('logout not implemented'); },
  user: undefined,
  loginWithPopup: async () => Promise.resolve(),
});


export function UnifiedAuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const {
    isAuthenticated: auth0Authenticated,
    isLoading,
    getAccessTokenSilently: auth0GetAccessTokenSilently,
    getIdTokenClaims: auth0GetIdTokenClaims,
    loginWithRedirect,
    logout: auth0Logout,
    user: auth0User,
    loginWithPopup,
  } = useAuth0();


  // Check for onboarding token in localStorage
  const onboardingToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  const isOnboardingAuthenticated = Boolean(onboardingToken);
  const onboardingClaims = decodeJwt(onboardingToken ?? '');

  // Unified isAuthenticated: true if either Auth0 or onboarding token is present
  const unifiedIsAuthenticated = auth0Authenticated || isOnboardingAuthenticated;

  // Unified isLoading: true only if Auth0 is loading and no onboarding token is present
  const unifiedIsLoading = isLoading && !isOnboardingAuthenticated;

  // Unified getAccessTokenSilently
  const getAccessTokenSilently = React.useCallback(async (): Promise<string | undefined> => {
    if (onboardingToken) return onboardingToken;
    return auth0GetAccessTokenSilently();
  }, [onboardingToken, auth0GetAccessTokenSilently]);


  // Type guard to check if claims match IdToken shape
  function isIdToken(claims: unknown): claims is IdToken {
    return (
      typeof claims === 'object'
      && claims !== null
      && 'iss' in claims
      && 'sub' in claims
    );
  }

  // Unified getIdTokenClaims
  const getIdTokenClaims = React.useCallback(async (): Promise<IdToken | undefined> => {
    if (onboardingClaims && isIdToken(onboardingClaims)) return onboardingClaims;
    return auth0GetIdTokenClaims();
  }, [onboardingClaims, auth0GetIdTokenClaims]);

  // Unified user
  const user: User | IdToken | undefined = onboardingClaims || auth0User;
  // Unified logout
  const logout = React.useCallback((options?: LogoutOptions): void => {
    if (onboardingToken) {
      localStorage.removeItem('accessToken');
      window.location.assign('/');
    }
    else {
      auth0Logout(options);
    }
  }, [onboardingToken, auth0Logout]);


  const unifiedLoginWithRedirect = React.useCallback(
    async (options?: RedirectLoginOptions) => loginWithRedirect(options),
    [loginWithRedirect],
  );

  const value = useMemo(
    () => ({
      isAuthenticated: unifiedIsAuthenticated,
      isLoading: unifiedIsLoading,
      getAccessTokenSilently,
      getIdTokenClaims,
      loginWithRedirect: unifiedLoginWithRedirect,
      logout,
      user,
      loginWithPopup,
    }),
    [
      unifiedIsAuthenticated,
      unifiedIsLoading,
      getAccessTokenSilently,
      getIdTokenClaims,
      unifiedLoginWithRedirect,
      logout,
      user,
      loginWithPopup,
    ],
  );

  return (
    <UnifiedAuthContext.Provider value={value}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}

export const useUnifiedAuth = () => useContext(UnifiedAuthContext);
